import { useState, useMemo } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CaretDown, Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Searchable dropdown replacement for shadcn Select for long option lists.
 * Props:
 *   value: string | null
 *   onValueChange: (id) => void
 *   options: [{ id, name, hint?, group? }]
 *   placeholder?: string
 *   pinnedIds?: string[]     -> options with these ids float to the top (e.g. Suspense)
 *   triggerClass?: string
 *   testId?: string
 *   emptyLabel?: string
 *   allowClear?: bool        -> shows "— None —" option
 *   size?: 'sm' | 'md'
 */
export default function ComboSelect({
    value,
    onValueChange,
    options = [],
    placeholder = "Choose…",
    pinnedIds = [],
    triggerClass = "",
    testId,
    emptyLabel = "No matches",
    allowClear = false,
    disabled = false,
    size = "md",
}) {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.id === value);

    const sortedOptions = useMemo(() => {
        if (!pinnedIds?.length) return options;
        const pinnedSet = new Set(pinnedIds);
        const pinned = options.filter((o) => pinnedSet.has(o.id));
        const rest = options.filter((o) => !pinnedSet.has(o.id));
        return [...pinned, ...rest];
    }, [options, pinnedIds]);

    const heightCls = size === "sm" ? "h-8" : "h-9";
    const textCls = size === "sm" ? "text-xs" : "text-sm";

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    aria-expanded={open}
                    className={cn(
                        heightCls, textCls,
                        "w-full justify-between font-normal bg-white",
                        !selected && "text-stone-400",
                        triggerClass
                    )}
                    data-testid={testId}
                >
                    <span className="truncate text-left">{selected ? (
                        <>
                            {selected.name}
                            {selected.hint ? <span className="text-stone-400 ml-1">· {selected.hint}</span> : null}
                        </>
                    ) : placeholder}</span>
                    <CaretDown size={12} className="ml-2 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search…" className="h-9" data-testid={testId ? `${testId}-search` : undefined} />
                    <CommandList className="max-h-[280px]">
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup>
                            {allowClear ? (
                                <CommandItem
                                    key="__clear__"
                                    value="__clear__"
                                    onSelect={() => { onValueChange?.(""); setOpen(false); }}
                                    className="text-stone-500"
                                >
                                    — None —
                                </CommandItem>
                            ) : null}
                            {sortedOptions.map((o) => (
                                <CommandItem
                                    key={o.id}
                                    // cmdk searches on the value string
                                    value={`${o.name} ${o.hint || ""} ${o.group || ""}`}
                                    onSelect={() => { onValueChange?.(o.id); setOpen(false); }}
                                    data-testid={testId ? `${testId}-opt-${o.id}` : undefined}
                                >
                                    <Check size={12} className={cn("mr-2", value === o.id ? "opacity-100" : "opacity-0")} />
                                    <div className="flex flex-col">
                                        <span>{o.name}</span>
                                        {o.hint ? <span className="text-[10px] text-stone-400">{o.hint}</span> : null}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
